import { useCallback, useEffect } from "react";
import {
  APIResponsePayload,
  EventPayload,
  isApiCallMessage,
  isApiResult,
  MessageType,
  StatusPayload,
} from "../utils";
import useUnmount from "../../common/ui/useUnmount";
import { noticeError } from "../../services/monitoring";
import { isPromise } from "../../common/utils/promise";
import { REQUEST_EXTERNAL_EVENT, RequiredFields } from "./events";

type ApiCall = (
  ...args: any[]
) =>
  | Promise<supernova.external.ApiResult>
  | supernova.external.ApiResult
  | number
  | string
  | undefined
  | null
  | void;

function postMessageUp(
  msg: StatusPayload | EventPayload | APIResponsePayload,
  origin: string = "*" // TODO: Scope origin if we can
) {
  if (window !== window.parent) {
    window.parent.postMessage(msg, origin);
  }
}

function emitExternalEvent(eventName: string, detail: any): void {
  postMessageUp({
    type: MessageType.EVENT,
    eventName,
    detail,
  });
}

function emitStatusEvent(applicationName: string, isReady: boolean): void {
  postMessageUp({ type: MessageType.STATUS, applicationName, isReady });
}

function emitApiResponse(uuid, result): void {
  postMessageUp({ uuid, type: MessageType.API_RESPONSE, result });
}

/**
 * This hook
 *   1. Sets up the ability to receive and process postMessage requests from
 *      the outer context, mostly to run method calls
 *   2. Sends setup/teardown messages, alerting the outer context of readiness
 *      to receive messages
 *   @param applicationName The name used to identify this application slice
 *   @param methodMap Available methods, keyed by callable name
 */
export default function useMessageRelay(
  applicationName: string,
  methodMap: Record<string, ApiCall>
) {
  const onMessageReceived = useCallback(
    (messageEvent: MessageEvent) => {
      if (isApiCallMessage(messageEvent)) {
        const { method, args, uuid } = messageEvent.data;
        if (methodMap[method]) {
          const result = methodMap[method](...args);
          // Massage result into an object with an `ok` field
          if (isPromise(result)) {
            result.then((data) => emitApiResponse(uuid, data));
          } else if (isApiResult(result)) {
            emitApiResponse(uuid, result);
          } else {
            emitApiResponse(uuid, { ok: true, data: result });
          }
        } else {
          noticeError(`useExternalAPI: No such method ${method}`);
        }
      }
    },
    [methodMap]
  );

  // Start listening for messages
  useEffect(() => {
    window.addEventListener("message", onMessageReceived);
    return () => {
      window.removeEventListener("message", onMessageReceived);
    };
  }, [onMessageReceived]);

  const sendStatusMessage = useCallback(
    (isReady) => {
      emitStatusEvent(applicationName, isReady);
    },
    [applicationName]
  );

  // Send ready event on mount
  useEffect(() => {
    sendStatusMessage(true);
  }, [sendStatusMessage]);

  const sendUnmountMessage = useCallback(() => {
    sendStatusMessage(false);
  }, [sendStatusMessage]);

  // Send not-ready event on unmount
  useUnmount(sendUnmountMessage);

  // Send not-ready event on navigation away
  useEffect(() => {
    window.addEventListener("beforeunload", sendUnmountMessage);
    return () => window.removeEventListener("beforeunload", sendUnmountMessage);
  }, [sendUnmountMessage]);

  // listen for events to emit
  useEffect(() => {
    function cb(event: CustomEvent<RequiredFields>) {
      emitExternalEvent(event.detail.eventName, event.detail.detail);
    }
    window.addEventListener(REQUEST_EXTERNAL_EVENT, cb);
    return () => window.removeEventListener(REQUEST_EXTERNAL_EVENT, cb);
  }, []);
}
